import { useState, useCallback, useEffect } from "react";
import { fetchJobs, fetchJob, stopJob, fetchRecentAppearances } from "../api/jobsApi";
import { startScan, startJobNow } from "../api/workerApi";
import { buildReportAddress } from "../data/reportAddress";

const POLL_MS = 3000;
const LIVE_PREVIEW_ROWS = 8;

// Jobs: the My Jobs list, starting and stopping a scan, watching it run,
// and choosing which job's report to show (the report loads its own data).
// App.js owns which screen is showing; this hook switches to "scanning"
// and "report" at the right moments via setScreen.
// agencyId: the signed-in account; the Scans list only shows its scans.
export default function useJobManager({ screen, setScreen, agencyId }) {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [recentAppearances, setRecentAppearances] = useState([]);
  // The job whose report is showing (its keywords and locations head the report).
  const [reportJob, setReportJob] = useState(null);

  const refreshJobs = useCallback(async () => {
    setJobsLoading(true);
    setJobs(await fetchJobs(agencyId));
    setJobsLoading(false);
  }, [agencyId]);

  const loadFinishedJob = useCallback(async (job) => {
    setReportJob(job);
    setScreen("report");
  }, [setScreen]);

  // Running jobs open the scanning screen; finished ones open the report.
  const openJob = (job) => {
    if (job.status === "running") {
      setActiveJobId(job.id);
      setScreen("scanning");
    } else {
      loadFinishedJob(job);
    }
  };

  // Opens a job from its id (a report address after a refresh or a copied
  // link). Returns false if the job can't be loaded.
  const openJobById = useCallback(async (jobId) => {
    const job = await fetchJob(jobId);
    if (!job) return false;
    if (job.status === "running") {
      setActiveJobId(job.id);
      setScreen("scanning");
    } else {
      loadFinishedJob(job);
    }
    return true;
  }, [loadFinishedJob, setScreen]);

  // Poll the active job while watching it scan
  useEffect(() => {
    if (screen !== "scanning" || !activeJobId) return;

    const poll = async () => {
      const jobRow = await fetchJob(activeJobId);
      if (!jobRow) return;
      setActiveJob(jobRow);

      setRecentAppearances(await fetchRecentAppearances(activeJobId, LIVE_PREVIEW_ROWS));

      if (jobRow.status === "completed" || jobRow.status === "stopped") {
        await loadFinishedJob(jobRow);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => clearInterval(interval);
  }, [screen, activeJobId, loadFinishedJob]);

  // The Worker creates the job and its samples; then we show the scanning
  // screen and ask the Worker to start sample 1.
  // scanRequest is built by buildScanRequest (core/data/scanPlan).
  const startJob = useCallback(async (scanRequest) => {
    if (scanRequest.keywords.length === 0 || scanRequest.postcodes.length === 0) return;

    const { jobId, error } = await startScan(scanRequest);
    if (error) {
      alert(`Couldn't start this search: ${error}`);
      return;
    }
    const job = await fetchJob(jobId);
    if (!job) {
      alert("The search started but couldn't be loaded. Check Audits.");
      return;
    }

    setActiveJob(job);
    setActiveJobId(job.id);
    setRecentAppearances([]);
    setScreen("scanning");

    await startJobNow(job.id);
  }, [setScreen]);

  // A website match from the Audits search: opens Advertiser Snapshot with
  // that website pinned (the report reads both from the address). A running
  // scan opens its scanning screen as usual.
  const openJobPinned = (job, pin) => {
    if (job.status !== "running") {
      window.history.replaceState(null, "", `${window.location.pathname}${buildReportAddress({ jobId: job.id, tab: "advertiser", pin })}`);
    }
    openJob(job);
  };

  const stopActiveJob = async () => {
    if (!activeJobId) return;
    await stopJob(activeJobId);
  };

  // Stop from the Audits list, then reload the list.
  const stopListedJob = async (jobId) => {
    await stopJob(jobId);
    await refreshJobs();
  };

  const clearActiveJob = () => {
    setActiveJobId(null);
    setActiveJob(null);
    setRecentAppearances([]);
  };

  return {
    reportJob,
    jobs, jobsLoading, refreshJobs,
    activeJob, recentAppearances,
    openJob, openJobPinned, openJobById, startJob, stopActiveJob, stopListedJob, clearActiveJob,
  };
}
